import { useEffect, useId, useState, type ReactNode } from 'react'
import {
  CalendarBlank,
  CaretDown,
  CheckCircle,
  DownloadSimple,
  FilePdf,
  HouseSimple,
  WarningCircle,
} from '@phosphor-icons/react'
import {
  IconArrowNarrowRight,
  IconBriefcase,
  IconBulb,
  IconChartBar,
  IconChecklist,
  IconRoute,
  IconPlayerPlay,
  IconUsersGroup,
} from '@tabler/icons-react'
import { PartnerQuote } from '@/legacy/components/sections/PartnerQuote'
import { DisplayHeading, LeadText } from '@/legacy/components/ui/Typography'

type BreadcrumbItem = {
  label: string
  href?: string
}

type TocItem = {
  id: string
  label: string
}

type MetaItem = {
  label: string
  value: string
}

type CaseComparisonPanel = {
  title: string
  body: string
  items: string[]
  metrics: MetaItem[]
}

type CaseDecisionProps = {
  id?: string
  title: string
  children: ReactNode
  quote: string
  cite: string
  image?: {
    url?: string
    alternativeText?: string
  }
  logo?: {
    url?: string
    alternativeText?: string
  }
  logoLabel?: string
  concernsTitle: string
  concerns: string[]
}

type CaseDecisionMakingProps = {
  id?: string
  title: string
  children: ReactNode
  groups: { title: string; items: string[] }[]
  examplesTitle: string
  examples: AccordionItem[]
}

type ArticleButtonProps = {
  href?: string
  children: ReactNode
  variant?: 'primary' | 'secondary' | 'ghost'
  icon?: ReactNode
  onClick?: () => void
}

type CalloutVariant = 'note' | 'important' | 'warning' | 'nb' | 'tip' | 'legal'

type AccordionItem = {
  title: string
  children: ReactNode
}

type TableRow = {
  cells: string[]
} | {
  label: string
  current: string
  automated: string
  owner: string
}

type DeadlineItem = {
  date: string
  label: string
  detail: string
}

type DownloadItem = {
  title: string
  meta: string
  href: string
}

type ArticleAuthorSectionProps = {
  author: {
    name: string
    role: string
    url?: string
    image: {
      src: string
      alt: string
    }
  }
  publishedAt: string
}

export const ArticleBreadcrumbs = ({ items }: { items: BreadcrumbItem[] }) => (
  <nav className="article-kit-breadcrumbs" aria-label="Breadcrumb">
    {items.map((item, index) => (
      <span key={item.label} className="article-kit-breadcrumbs__item">
        {item.href ? (
          <a href={item.href} aria-label={index === 0 ? item.label : undefined}>
            {index === 0 ? <HouseSimple size={12} weight="fill" aria-hidden /> : item.label}
          </a>
        ) : (
          <span>{item.label}</span>
        )}
        {index < items.length - 1 ? <span aria-hidden>/</span> : null}
      </span>
    ))}
  </nav>
)

export const ArticleHeroImage = ({ src, alt }: { src: string; alt: string }) => (
  <figure className="article-kit-hero-image">
    <img src={src} alt={alt} loading="eager" decoding="async" fetchPriority="high" />
  </figure>
)

export const ArticleH1 = ({ children, className = '' }: { children: ReactNode; className?: string }) => (
  <DisplayHeading className={className}>{children}</DisplayHeading>
)

export const ArticleTldr = ({ children, label }: { children: ReactNode; label?: string }) => (
  <div className="article-kit-tldr">
    {label ? <p className="article-kit-tldr__label">{label}</p> : null}
    <LeadText>{children}</LeadText>
  </div>
)

export const ArticleCoverImage = ({ src, alt }: { src: string; alt: string }) => (
  <figure className="article-kit-cover-image">
    <img src={src} alt={alt} loading="eager" decoding="async" fetchPriority="high" />
  </figure>
)

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const formatBylineDate = (value: string) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`
}

const formatNumericDate = (value: string) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const day = String(date.getUTCDate()).padStart(2, '0')
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  return `${day}.${month}.${date.getUTCFullYear()}`
}

export const ArticleByline = ({
  author,
  date,
}: {
  author?: { name: string; url?: string }
  date?: string
}) => {
  const published = date ? formatBylineDate(date) : ''

  if (!author && !published) return null

  return (
    <p className="article-kit-byline">
      {author ? (
        <span>
          Written by{' '}
          {author.url ? (
            <a href={author.url} target="_blank" rel="noopener noreferrer">
              {author.name}
            </a>
          ) : (
            <strong>{author.name}</strong>
          )}
        </span>
      ) : null}
      {author && published ? <span aria-hidden> · </span> : null}
      {published ? (
        <span>
          Published <time dateTime={date}>{published}</time>
        </span>
      ) : null}
    </p>
  )
}

export const ArticleAuthorSection = ({ author, publishedAt }: ArticleAuthorSectionProps) => {
  const published = formatNumericDate(publishedAt)
  const authorName = `Article by ${author.name}`

  return (
    <footer className="article-kit-author-section">
      <div className="article-kit-author-section__person">
        <img src={author.image.src} alt={author.image.alt} loading="lazy" decoding="async" />
        <div>
          <p className="article-kit-author-section__name">
            {author.url ? (
              <a href={author.url} target="_blank" rel="noopener noreferrer">
                {authorName}
              </a>
            ) : (
              authorName
            )}
          </p>
          <p className="article-kit-author-section__role">{author.role}</p>
        </div>
      </div>
      {published ? (
        <div className="article-kit-author-section__date">
          <p>Published:</p>
          <time dateTime={publishedAt}>{published}</time>
        </div>
      ) : null}
    </footer>
  )
}

export const ArticleButton = ({
  href,
  children,
  variant = 'primary',
  icon,
  onClick,
}: ArticleButtonProps) => {
  const className = `ui-button ui-button--${variant} article-kit-button`
  const content = (
    <>
      <span>{children}</span>
      {icon ?? (variant === 'primary' ? <IconArrowNarrowRight size={24} stroke={2} aria-hidden /> : null)}
    </>
  )

  if (href) {
    return (
      <a className={className} href={href}>
        {content}
      </a>
    )
  }

  return (
    <button className={className} type="button" onClick={onClick}>
      {content}
    </button>
  )
}

export const ArticleLayout = ({
  toc,
  rail,
  children,
}: {
  toc?: ReactNode
  rail?: ReactNode
  children: ReactNode
}) => (
  <div className="article-kit-layout">
    <div className="article-kit-layout__main">{children}</div>
    {toc ? <aside className="article-kit-layout__toc">{toc}</aside> : null}
    {rail ? <aside className="article-kit-layout__rail">{rail}</aside> : null}
  </div>
)

export const ArticleToc = ({ items }: { items: TocItem[] }) => {
  const [activeId, setActiveId] = useState(items[0]?.id ?? '')
  const [isOpen, setIsOpen] = useState(false)
  const listId = useId()
  const activeItem = items.find((item) => item.id === activeId) ?? items[0]

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      return undefined
    }

    const sections = items
      .map((item) => document.getElementById(item.id))
      .filter((section): section is HTMLElement => Boolean(section))

    if (!sections.length) {
      return undefined
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]

        if (visibleEntry?.target.id) {
          setActiveId(visibleEntry.target.id)
        }
      },
      {
        rootMargin: '-24% 0px -58% 0px',
        threshold: [0.1, 0.35, 0.6],
      },
    )

    sections.forEach((section) => observer.observe(section))

    return () => observer.disconnect()
  }, [items])

  return (
    <nav className="article-kit-toc" aria-label="On this page">
      <p>On this page you will find</p>
      <button
        className="article-kit-toc__toggle"
        type="button"
        aria-expanded={isOpen}
        aria-controls={listId}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span>On this page</span>
        <strong>{activeItem?.label ?? 'Sections'}</strong>
        <CaretDown size={20} weight="bold" aria-hidden />
      </button>
      <ol id={listId} data-open={isOpen}>
        {items.map((item) => (
          <li key={item.id}>
            <a
              className={item.id === activeId ? 'article-kit-toc__link article-kit-toc__link--active' : 'article-kit-toc__link'}
              href={`#${item.id}`}
              aria-current={item.id === activeId ? 'location' : undefined}
              onClick={() => setIsOpen(false)}
            >
              {item.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  )
}

export const ArticleSection = ({
  id,
  title,
  children,
  level = 2,
  icon,
}: {
  id: string
  title: string
  children: ReactNode
  level?: 2 | 3 | 4
  icon?: ReactNode
}) => {
  const Heading = `h${level}` as 'h2' | 'h3' | 'h4'

  return (
    <section id={id} className="article-kit-section">
      {icon ? <span className="article-kit-section__icon">{icon}</span> : null}
      <Heading className={`article-kit-h${level}`}>{title}</Heading>
      {children}
    </section>
  )
}

export const ArticleHeading = ({ level, children }: { level: 2 | 3 | 4; children: ReactNode }) => {
  const Heading = `h${level}` as 'h2' | 'h3' | 'h4'

  return <Heading className={`article-kit-h${level}`}>{children}</Heading>
}

export const ArticleParagraph = ({ children }: { children: ReactNode }) => (
  <p className="article-kit-p">{children}</p>
)

export const ArticleInlineLink = ({ href, children }: { href: string; children: ReactNode }) => (
  <a className="article-kit-inline-link" href={href}>
    {children}
  </a>
)

const ArticleFactIcon = ({ label }: { label: string }) => {
  const normalizedLabel = label.toLowerCase()

  if (normalizedLabel.includes('client')) {
    return <IconUsersGroup size={48} stroke={2} aria-hidden />
  }

  return <IconBriefcase size={48} stroke={2} aria-hidden />
}

type ArticleSectionIconVariant = 'results' | 'decision' | 'solution' | 'learnings' | 'video'

export const ArticleSectionIcon = ({ variant = 'results' }: { variant?: ArticleSectionIconVariant }) => {
  const iconProps = { size: 32, stroke: 1.7, 'aria-hidden': true as const }

  if (variant === 'decision') return <IconRoute {...iconProps} />
  if (variant === 'solution') return <IconChecklist {...iconProps} />
  if (variant === 'learnings') return <IconBulb {...iconProps} />
  if (variant === 'video') return <IconPlayerPlay {...iconProps} />

  return <IconChartBar {...iconProps} />
}

const ArticleDefinitionList = ({
  items,
  className,
  renderPrefix,
}: {
  items: MetaItem[]
  className?: string
  renderPrefix?: (item: MetaItem) => ReactNode
}) => (
  <dl className={className}>
    {items.map((item) => (
      <div key={item.label}>
        {renderPrefix?.(item)}
        <dt>{item.label}</dt>
        <dd>{item.value}</dd>
      </div>
    ))}
  </dl>
)

const ArticleListItems = ({
  items,
  renderMarker,
  renderContent,
}: {
  items: string[]
  renderMarker: (item: string, index: number) => ReactNode
  renderContent?: (item: string, index: number) => ReactNode
}) => (
  <>
    {items.map((item, itemIndex) => (
      <li key={`${item}-${itemIndex}`}>
        {renderMarker(item, itemIndex)}
        {renderContent?.(item, itemIndex) ?? <span>{item}</span>}
      </li>
    ))}
  </>
)

const ArticleIconListItems = ({
  items,
  icon = 'check',
}: {
  items: string[]
  icon?: 'check' | 'warning'
}) => (
  <ArticleListItems
    items={items}
    renderMarker={() =>
      icon === 'warning' ? (
        <WarningCircle size={24} weight="fill" aria-hidden />
      ) : (
        <CheckCircle size={24} weight="fill" aria-hidden />
      )
    }
  />
)

export const ArticleCardListGroup = ({
  groups,
}: {
  groups: { title: string; items: string[]; icon?: 'check' | 'warning' }[]
}) => (
  <div className="article-kit-applies">
    {groups.map((group, groupIndex) => (
      <div key={group.title}>
        <h3>{group.title}</h3>
        <ul>
          <ArticleIconListItems
            items={group.items}
            icon={group.icon ?? (groupIndex === 0 ? 'check' : 'warning')}
          />
        </ul>
      </div>
    ))}
  </div>
)

export const ArticleKeyFacts = ({ facts }: { facts: MetaItem[] }) => (
  <ArticleDefinitionList
    className="article-kit-key-facts"
    items={facts}
    renderPrefix={(fact) => (
      <span className="article-kit-key-facts__icon">
        <ArticleFactIcon label={fact.label} />
      </span>
    )}
  />
)

export const ArticleVideoSection = ({
  id,
  title,
  videoTitle,
  src,
  type = 'video/mp4',
  poster,
}: {
  id?: string
  title: string
  videoTitle: string
  src: string
  type?: string
  poster?: string
}) => (
  <section id={id} className="article-kit-section article-kit-video-section">
    <h2 className="article-kit-h2">{title}</h2>
    <div className="article-kit-video-section__frame">
      <video title={videoTitle} controls playsInline preload="metadata" poster={poster}>
        <source src={src} type={type} />
      </video>
    </div>
  </section>
)

export const ArticlePodcastSection = ({
  id,
  title,
  src,
  embedTitle,
}: {
  id?: string
  title: string
  src: string
  embedTitle?: string
}) => (
  <section id={id} className="article-kit-section article-kit-podcast-section">
    <h2 className="article-kit-h2">{title}</h2>
    <iframe
      className="article-kit-podcast-section__embed"
      title={embedTitle ?? title}
      src={src}
      width="100%"
      height="152"
      loading="lazy"
      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
      allowFullScreen
    />
  </section>
)

export const ArticleAppliesTo = ({
  applies,
  excludes,
}: {
  applies: string[]
  excludes: string[]
}) => (
  <ArticleCardListGroup
    groups={[
      { title: 'Applies to', items: applies, icon: 'check' },
      { title: 'Does not apply to', items: excludes, icon: 'warning' },
    ]}
  />
)

export const ArticleAppliesGroups = ({
  id,
  groups,
  title,
  body,
  icon,
}: {
  id?: string
  groups: { title: string; items: string[] }[]
  title?: string
  body?: ReactNode
  icon?: ReactNode
}) => {
  const cards = <ArticleCardListGroup groups={groups} />

  if (!title && !body && !icon) return cards

  return (
    <section id={id} className="case-study-applies-section">
      {icon ? <span className="article-kit-section__icon">{icon}</span> : null}
      {title ? <h2 className="article-kit-h2">{title}</h2> : null}
      {body ? <div className="article-kit-block-prose">{body}</div> : null}
      {cards}
    </section>
  )
}

export const ArticleCallout = ({
  title,
  children,
  variant = 'note',
}: {
  title: string
  children: ReactNode
  variant?: CalloutVariant
}) => (
  <aside className={`article-kit-callout article-kit-callout--${variant}`}>
    <div className="article-kit-callout__heading">
      <WarningCircle size={24} weight="fill" aria-hidden />
      <h3>{title}</h3>
    </div>
    <div className="article-kit-callout__body">{children}</div>
  </aside>
)

export const ArticleQuote = ({
  quote,
  cite,
  variant,
  image,
}: {
  quote: string
  cite: string
  variant?: 'profile'
  image?: {
    url?: string
    alternativeText?: string
  }
}) => {
  return (
    <PartnerQuote
      variant={variant === 'profile' ? 'profile' : 'article'}
      quote={quote}
      cite={cite}
      media={image?.url ? { src: image.url, alt: image.alternativeText } : undefined}
    />
  )
}

export const ArticleCaseComparison = ({
  id,
  before,
  after,
}: {
  id?: string
  before: CaseComparisonPanel
  after: CaseComparisonPanel
}) => (
  <section id={id} className="case-study-comparison">
    {[before, after].map((panel, index) => {
      const className = [
        'case-study-comparison__panel',
        index === 1 ? 'case-study-comparison__panel--dark' : '',
        panel.metrics.length ? '' : 'case-study-comparison__panel--compact',
      ]
        .filter(Boolean)
        .join(' ')

      return (
        <article key={panel.title} className={className}>
          <div>
            <h2>{panel.title}</h2>
            <p>{panel.body}</p>
          </div>
          <ul>
            {panel.items.map((item, itemIndex) => (
              <li key={`${item}-${itemIndex}`}>
                {index === 0 ? (
                  <WarningCircle size={24} weight="fill" aria-hidden />
                ) : (
                  <CheckCircle size={24} weight="fill" aria-hidden />
                )}
                <span>{item}</span>
              </li>
            ))}
          </ul>
          {panel.metrics.length ? <ArticleDefinitionList items={panel.metrics} /> : null}
        </article>
      )
    })}
  </section>
)

export const ArticleCaseDecision = ({
  id,
  title,
  children,
  quote,
  cite,
  image,
  logo,
  logoLabel,
  concernsTitle,
  concerns,
}: CaseDecisionProps) => (
  <section id={id} className="case-study-decision">
    <div className="case-study-decision__intro">
      <h2 className="article-kit-h2">{title}</h2>
      {children}
    </div>
    <PartnerQuote
      variant="case"
      quote={quote}
      cite={cite}
      media={image?.url ? { src: image.url, alt: image.alternativeText } : undefined}
      logo={logo?.url ? { src: logo.url, alt: logo.alternativeText, label: logoLabel } : { label: logoLabel || 'Client logo' }}
    />
    <div className="case-study-decision__concerns">
      <h3>{concernsTitle}</h3>
      <ul>
        <ArticleIconListItems items={concerns} icon="warning" />
      </ul>
    </div>
  </section>
)

export const ArticleCaseDecisionMaking = ({
  id,
  title,
  children,
  groups,
  examplesTitle,
  examples,
}: CaseDecisionMakingProps) => {
  const [openItem, setOpenItem] = useState<number | null>(0)

  const toggleItem = (index: number) => {
    setOpenItem((current) => (current === index ? null : index))
  }

  return (
    <section id={id} className="case-study-decision-making">
      <div className="case-study-decision-making__intro">
        <span className="article-kit-section__icon">
          <ArticleSectionIcon variant="decision" />
        </span>
        <h2 className="article-kit-h2">{title}</h2>
        {children}
      </div>
      <div className="case-study-decision-making__grid">
        <div className="case-study-decision-making__groups">
          {groups.map((group, groupIndex) => (
            <article
              key={group.title}
              className={
                groupIndex === 0
                  ? 'case-study-decision-making__group case-study-decision-making__group--dark'
                  : 'case-study-decision-making__group'
              }
            >
              <h3>{group.title}</h3>
              <ul>
                <ArticleIconListItems items={group.items} icon="check" />
              </ul>
            </article>
          ))}
        </div>
        <article className="case-study-decision-making__examples">
          <div className="case-study-decision-making__examples-header">
            <h3>{examplesTitle}</h3>
          </div>
          <div className="article-kit-accordion article-kit-accordion--simple">
            <div className="article-kit-accordion__items">
              {examples.map((example, index) => {
                const isOpen = openItem === index

                return (
                  <section key={`${example.title}-${index}`} className="article-kit-accordion__item">
                    <button type="button" aria-expanded={isOpen} onClick={() => toggleItem(index)}>
                      <span>{example.title}</span>
                      <CaretDown size={24} weight="bold" aria-hidden />
                    </button>
                    {isOpen ? <div className="article-kit-accordion__content">{example.children}</div> : null}
                  </section>
                )
              })}
            </div>
          </div>
        </article>
      </div>
    </section>
  )
}

export const ArticleCaseMetricList = ({ id, title, metrics }: { id?: string; title: string; metrics: MetaItem[] }) => (
  <section id={id} className="case-study-metrics" aria-labelledby={id ? `${id}-title` : undefined}>
    <h2 id={id ? `${id}-title` : undefined} className="article-kit-h2">{title}</h2>
    <ArticleDefinitionList items={metrics} />
  </section>
)

export const ArticleCaseLink = ({ href }: { href: string; label: string }) => (
  <div className="case-study-link-row">
    <a href={href}>
      <span>Read more about the solution</span>
      <IconArrowNarrowRight size={24} stroke={2} aria-hidden />
    </a>
  </div>
)

export const ArticleChecklist = ({ title, items }: { title: string; items: string[] }) => (
  <div className="article-kit-checklist">
    <h3>{title}</h3>
    <ul>
      <ArticleIconListItems items={items} icon="check" />
    </ul>
  </div>
)

export const ArticleSteps = ({ steps }: { steps: string[] }) => (
  <ol className="article-kit-steps">
    <ArticleListItems
      items={steps}
      renderMarker={(_step, index) => <span>{index + 1}</span>}
      renderContent={(step) => <p>{step}</p>}
    />
  </ol>
)

export const ArticleDeadlineGrid = ({ items }: { items: DeadlineItem[] }) => (
  <div className="article-kit-deadlines">
    {items.map((item) => (
      <article key={item.label}>
        <div className="article-kit-deadlines__meta">
          <CalendarBlank size={24} weight="fill" aria-hidden />
          <time>{item.date}</time>
        </div>
        <h3>{item.label}</h3>
        <p>{item.detail}</p>
      </article>
    ))}
  </div>
)

export const ArticleTable = ({
  headers = ['Workflow area', 'Manual state', 'Automated state', 'Owner'],
  rows,
}: {
  headers?: string[]
  rows: TableRow[]
}) => (
  <div className="article-kit-table-wrap">
    <table className="article-kit-table">
      <thead>
        <tr>
          {headers.map((header) => (
            <th key={header}>{header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => {
          const cells = 'cells' in row ? row.cells : [row.label, row.current, row.automated, row.owner]

          return (
          <tr key={`${cells[0]}-${rowIndex}`}>
            {cells.map((cell, cellIndex) =>
              cellIndex === 0 ? <th key={cell}>{cell}</th> : <td key={cell}>{cell}</td>,
            )}
          </tr>
          )
        })}
      </tbody>
    </table>
  </div>
)

export const ArticleDefinitions = ({ items }: { items: MetaItem[] }) => (
  <ArticleDefinitionList className="article-kit-definitions" items={items} />
)

export const ArticleFaqSection = ({
  id,
  title,
  items,
  icon,
  body,
  showControls = true,
}: {
  id: string
  title: string
  items: AccordionItem[]
  icon?: ReactNode
  body?: ReactNode
  showControls?: boolean
}) => {
  const [openItems, setOpenItems] = useState<number[]>([])
  const allOpen = openItems.length === items.length

  const toggleAll = () => {
    setOpenItems(allOpen ? [] : items.map((_, index) => index))
  }

  const toggleItem = (index: number) => {
    setOpenItems((current) =>
      current.includes(index) ? current.filter((item) => item !== index) : [...current, index],
    )
  }

  return (
    <section id={id} className="article-kit-section article-kit-faq-section">
      {icon ? <span className="article-kit-section__icon">{icon}</span> : null}
      <div className="article-kit-section-heading-row">
        <h2 className="article-kit-h2">{title}</h2>
        {showControls ? (
          <button type="button" onClick={toggleAll}>
            {allOpen ? 'Collapse all' : 'Expand all'}
          </button>
        ) : null}
      </div>
      {body ? <div className="article-kit-faq-section__body">{body}</div> : null}
      <div className="article-kit-accordion article-kit-accordion--simple">
        <div className="article-kit-accordion__items">
          {items.map((item, index) => {
            const isOpen = openItems.includes(index)

            return (
              <section key={item.title} className="article-kit-accordion__item">
                <button type="button" aria-expanded={isOpen} onClick={() => toggleItem(index)}>
                  <span>{item.title}</span>
                  <CaretDown size={24} weight="bold" aria-hidden />
                </button>
                {isOpen ? <div className="article-kit-accordion__content">{item.children}</div> : null}
              </section>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export const ArticleDecisionTree = ({
  items,
}: {
  items: { question: string; yes: string; no: string }[]
}) => (
  <div className="article-kit-decision-tree">
    {items.map((item) => (
      <article key={item.question}>
        <h3>{item.question}</h3>
        <div className="article-kit-decision-tree__branches">
          <p>
            <strong>Yes</strong>
            <span>{item.yes}</span>
          </p>
          <p>
            <strong>No</strong>
            <span>{item.no}</span>
          </p>
        </div>
      </article>
    ))}
  </div>
)

export const ArticleDownloads = ({ items }: { items: DownloadItem[] }) => (
  <div className="article-kit-downloads">
    {items.map((item) => (
      <a key={item.title} href={item.href}>
        <FilePdf size={24} weight="fill" aria-hidden />
        <span>
          <strong>{item.title}</strong>
          <small>{item.meta}</small>
        </span>
        <DownloadSimple size={24} weight="bold" aria-hidden />
      </a>
    ))}
  </div>
)

export const ArticleContactBlock = () => (
  <section id="audit-contact" className="article-kit-contact">
    <div>
      <h2>Need this mapped to your firm?</h2>
      <p>Send one monthly workflow and we will identify the first safe automation path.</p>
    </div>
    <ArticleButton href="/contact" variant="primary">
      Book audit
    </ArticleButton>
  </section>
)
